import { useState, useEffect } from "react";
import { ensureSupabaseAuthSession, useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";

const CACHE_KEY = "fastblog_super_admin";

interface CachedSuperAdmin {
  isSuperAdmin: boolean;
  userId: string;
}

function getCachedSuperAdmin(userId: string | undefined): CachedSuperAdmin | null {
  if (!userId) return null;
  try {
    const cached = sessionStorage.getItem(CACHE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached) as CachedSuperAdmin;
      // Only use cache if it's for the same user
      if (parsed.userId === userId) {
        return parsed;
      }
    }
  } catch {
    // Ignore cache errors
  }
  return null;
}

function setCachedSuperAdmin(userId: string, isSuperAdmin: boolean) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ isSuperAdmin, userId }));
  } catch {
    // Ignore cache errors
  }
}

export function useSuperAdmin() {
  const { user, session, isLoading: authLoading } = useAuth();
  const userId = user?.id;

  // Cache só vale para o mesmo usuário; evita piscar enquanto confere no servidor.
  const cached = getCachedSuperAdmin(userId);
  const [result, setResult] = useState<{ userId: string | null; isSuperAdmin: boolean } | null>(
    cached ? { userId: cached.userId, isSuperAdmin: cached.isSuperAdmin } : null,
  );

  useEffect(() => {
    let cancelled = false;
    if (authLoading) return;
    if (!userId) {
      setResult({ userId: null, isSuperAdmin: false });
      return;
    }

    async function check() {
      const fromCache = getCachedSuperAdmin(userId);
      if (fromCache) setResult({ userId: userId!, isSuperAdmin: fromCache.isSuperAdmin });

      for (let attempt = 0; attempt < 3; attempt++) {
        if (cancelled) return;
        try {
          const authReady = await ensureSupabaseAuthSession(session);
          if (authReady) {
            const { data, error } = await supabase.rpc("is_super_admin", { _user_id: userId! });
            if (!error) {
              if (cancelled) return;
              const isAdmin = data === true;
              setCachedSuperAdmin(userId!, isAdmin);
              setResult({ userId: userId!, isSuperAdmin: isAdmin });
              return;
            }
            console.warn("[useSuperAdmin] tentativa falhou:", error.message);
          }
        } catch (err) {
          console.warn("[useSuperAdmin] tentativa falhou:", err);
        }
        await new Promise((r) => setTimeout(r, 800 * (attempt + 1)));
      }
      // Sem resposta confiável: usa o cache se houver; senão nega (servidor protege os dados).
      if (cancelled) return;
      setResult({ userId: userId!, isSuperAdmin: fromCache?.isSuperAdmin ?? false });
    }

    check();
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, authLoading]);

  // Só decide depois de conferir o usuário ATUAL — fecha a corrida que
  // mandava o super admin para fora do /admin.
  const isLoading = authLoading || !result || result.userId !== (userId ?? null);
  const isSuperAdmin = !isLoading && !!result?.isSuperAdmin;
  return { isSuperAdmin, isLoading };
}
